import {
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Stack,
  Grid,
} from "@mui/material";
import { GENERIC_MAP } from "../../utils/genericMap";
import StyledTooltip from "../../utils/styledToolTip";
import { formatDecimal } from "../../utils/numberFormat";

export default function GenericTable({ projects, title, forPdf=false, tooltip="" }) {

  const roundToTwo = (value) => Math.round((value + Number.EPSILON) * 100) / 100;

  const formatWithSpaceThousands = (value) => {
    if (value === null || value === undefined || isNaN(value)) return "-";
    return formatDecimal(value);
  }

  // Helper para encontrar a secção pelo título
  const findSectionByTitle = (indicatorMap, title) =>
    Array.isArray(indicatorMap)
      ? indicatorMap.find(
          (s) =>
            s &&
            typeof s.title === "string" &&
            s.title.trim().toLowerCase() === String(title).trim().toLowerCase()
        )
      : undefined;

  const getIndicatorsFromSection = (indicatorMap, title) => {
    const section = findSectionByTitle(indicatorMap, title);
    return Array.isArray(section?.indicators) ? section.indicators : [];
  };

  const section = findSectionByTitle(GENERIC_MAP, title);
  const indicators = getIndicatorsFromSection(GENERIC_MAP, title);
  
  const getByPath = (obj, path) => {
    if (!obj || !path) return undefined;
    return path.split(".").reduce((acc, key) => 
      acc == null ? acc : acc[key], 
      obj
    );
  };

  function getMetricValue(project, key) {
    if (!project || !key) return undefined;

    // 1) Procurar dentro de projectInfo
    if (project.projectInfo && key in project.projectInfo) {
      return project.projectInfo[key];
    }

    // 2) Procurar na raiz do projeto (fallback)
    if (key in project) {
      return project[key];
    }

    // 3) Fallback final: tentar caminho explícito "projectInfo.key"
    return getByPath(project, `projectInfo.${key}`);
  }
 
  return (
    <Box>
      <Typography
        variant="h6"
        align="left"
        sx={{ fontWeight: 700, mb: 3, mt: 6, ...(forPdf && {mt: 1, mb: 1}) }}
      >
        {section.title}
      </Typography>

       {/* --- Tabela --- */}
      <TableContainer sx={{ width: "100%" }}>
        <Table stickyHeader sx={{ mt: 0, tableLayout: "fixed", width: "100%" }}>
          <TableHead>
            <TableRow
              sx={{
                "& > .MuiTableCell-root": {
                  borderBottom: "1px solid #e0e0e0 !important",
                },
              }}
            >
              <TableCell sx={{ width: "100%" }}>
                <Typography
                  variant="subtitle1"
                  color="text.secondary"
                  align="left"
                  sx={{ fontWeight: 600, px: 0, mr:1, display: "inline-block" }}
                >
                  {section.tableTitle}
                  
                </Typography> 
                {tooltip && <StyledTooltip title={tooltip} arrow><span className="icon"></span></StyledTooltip>}
              </TableCell>

              {/* uma coluna por projeto */}
              {projects.map((project) => (
                <TableCell
                  key={project.id ?? project.projectName}
                  align="right"
                  sx={{ width: 160, maxWidth: 160, px: 0 }}
                >
                  <Typography
                    variant="subtitle1"
                    color="text.secondary"
                    align="center"
                    sx={{ fontWeight: 600, px: 1 }}
                  >
                    {project.projectName}
                  </Typography>
                </TableCell>
              ))}

              <TableCell sx={{ width: 80, maxWidth: 80 }}>
                <Typography
                  variant="subtitle1"
                  color="text.secondary"
                  align="center"
                  sx={{ fontWeight: 600, px: 1 }}
                >
                  Unit
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {indicators.map((m, idxCond) => (
              <TableRow key={m.key} aria-label={m.key}>
                {/* Condição */}
                <TableCell
                  sx={{
                    width: `calc(100% - ${projects.length * 160}px)`,
                    py: 1.25,
                    borderBottom: "1px solid #e0e0e0 !important",
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {m.name}
                  </Typography>
                </TableCell>

                {/* Valores por projeto (em %) */}
                {projects.map((project, idxProj) => {
                  const val = getMetricValue(project, m.key);
                  const isNumber =
                    typeof val === "number" && Number.isFinite(val);
                  return (
                    <TableCell
                      key={(project.id ?? project.projectName) + ":" + m.key}
                      align="right"
                      sx={{
                        width: 160,
                        minWidth: 160,
                        maxWidth: 160,
                        borderBottom: "1px solid #e0e0e0 !important",
                      }}
                    >
                      <Typography
                        variant="body2"
                        align="center"
                        noWrap
                        sx={{
                          fontVariantNumeric: "tabular-nums",
                          //color: colorForProject(idxProj), // opcional
                        }}
                        title={isNumber ? String(val) : ""}
                      >
                        {isNumber ? formatWithSpaceThousands(roundToTwo(val)) : "--"}
                      </Typography>
                    </TableCell>
                  );
                })}

                {/* Unidade */}
                <TableCell
                  sx={{
                    width: 80,
                    maxWidth: 80,
                    px: 0,
                    borderBottom: "1px solid #e0e0e0 !important",
                  }}
                >
                  <Typography variant="body2" align="center" noWrap 
                    sx={{ fontSize: '0.74rem' }}>
                    {m.unit}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
    );
}